// Plan 08-07 — react-intl IntlProvider integration with $locale store.
//
// CONTEXT line 89 + RESEARCH §App Boot: IntlProvider is the OUTERMOST
// React provider — it wraps ThemeProvider, MotionProvider, AuthProvider,
// and the Router. This ordering matters because:
//   - ThemeProvider's loadTheme network errors don't need to be localized
//     (logged to console). Putting Intl outside it means Theme can render
//     before messages load.
//   - Auth + Router DO surface user-facing copy (sign-in error, 404), so
//     they MUST be inside IntlProvider.
//
// Lifecycle:
//   1. mount → read $locale store → setMessages(null) → trigger loadMessages
//   2. resolved → render <IntlProvider locale messages>{children}</...>
//   3. error → log + set messages={} so FormattedMessage falls through to
//      the literal id (which is the same key the test asserts against)
//
// MISSING_TRANSLATION suppression: react-intl emits a console.warn for
// every key not in the dictionary. We downgrade to console.warn at dev
// time only (logged once) and silence in production — Phase 9 launch
// readiness will tighten this to fail CI on missing keys.

import { useStore } from '@nanostores/react'
import { type ReactNode, useEffect, useState } from 'react'
import { IntlProvider, type ResolvedIntlConfig } from 'react-intl'

import { $locale } from '../stores/locale'
import { loadMessages } from './load'

interface IntlContextProviderProps {
  children: ReactNode
}

export function IntlContextProvider({
  children,
}: IntlContextProviderProps): ReactNode {
  const locale = useStore($locale)
  const [messages, setMessages] = useState<Record<string, string> | null>(null)

  useEffect(() => {
    let cancelled = false
    setMessages(null)
    loadMessages(locale)
      .then((m) => {
        if (cancelled) return
        setMessages(m)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('[runegambit] locale load failed:', err)
        // Empty dict — FormattedMessage will surface the key as fallback,
        // which is consistent with the dev-time MISSING_TRANSLATION
        // behaviour and lets the chrome render rather than blocking on a
        // network failure.
        setMessages({})
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  // First-paint suspends here until the active locale is loaded. The
  // splash background (var(--color-bg-deep)) renders underneath via
  // globals.css — there's no white flash, just the brand bg behind a
  // brief blank moment (≤1 RTT once the locales/*.json HEAD has been
  // cached by SW / browser HTTP cache).
  if (!messages) return null

  const onError: ResolvedIntlConfig['onError'] = (err) => {
    // MISSING_TRANSLATION + MISSING_DATA are non-fatal — log loudly in
    // dev so missing keys are visible during development, swallow in
    // production so a single missing key doesn't spam the console.
    if (err.code === 'MISSING_TRANSLATION' || err.code === 'MISSING_DATA') {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[i18n]', err.message)
      }
      return
    }
    // eslint-disable-next-line no-console
    console.error('[i18n]', err)
  }

  return (
    <IntlProvider
      locale={locale}
      messages={messages}
      defaultLocale="en"
      onError={onError}
    >
      {children}
    </IntlProvider>
  )
}
